<template>
  <section class="cs-hero">
    <header>
      <h1 data-testid="campsite-name1">
        {{ campsite.name }}
      </h1>

      <span
        v-scrollto="{
          target: '#pitch-types',
          options: { duration: 200, offset: scrollOffset },
        }"
        class="booking-info"
      >
        <translate v-if="!isDated && campsite.leadPrice" class="header-cta"
          >View prices & book</translate
        >
        <span
          v-else-if="!campsite.availableToSearch && campsite.isBookable"
          class="zero-availability-msg"
        >
          <img
            src="@/assets/images/warning.svg"
            width="15"
            height="15"
            alt=""
          />
          <translate> No availability at present on Pitchup.com </translate>
        </span>
        <translate v-else-if="isDated && campsite.isBookable" class="header-cta"
          >View prices & book</translate
        >
        <last-booked v-if="campsite.lastBooked" :date="campsite.lastBooked" />
      </span>

      <span
        v-if="campsite.rating"
        v-scrollto="{
          target: '#reviews',
          options: { duration: 200, offset: scrollOffset },
        }"
        class="reviews-info"
        ><pu-rating-bubble
          :rating="campsite.rating * 2"
          :rate-count="campsite.rateCount"
        />
        <translate :tt="{ count: campsite.rateCount }" class="reviews-meta">
          {count} reviews(s)
        </translate>
      </span>
    </header>

    <div class="hero">
      <div
        class="primary-image"
        :class="{ 'no-primary': !campsite.primaryPhoto }"
      >
        <pu-campsite-photos-provider
          v-if="campsite.primaryPhoto && photos?.length"
          :search-result-index="0"
          :campsite-id="campsite.id"
          :campsite-name="campsite.name"
          :category-ids="types"
          :gallery-full-screen="false"
          :primary-photo="primaryPhoto"
          :photos-prop="photos"
          :photo-count="photos.length"
          :load-photos-on-mount="false"
          @click="navigateToAlbums"
        />
        <new-campsite-badge
          v-if="campsite.firstAvailableDate"
          :first-available-date="campsite.firstAvailableDate"
        />
        <client-only>
          <campsite-favourite-button
            :campsite-id="campsite.id"
            :campsite-name="campsite.name"
            class="cs-favourite"
          />
        </client-only>
      </div>
      <div v-if="campsite.isBookable" class="secondary-image">
        <div
          v-for="(heroImage, index) in secondaryHeroImages"
          :key="index"
          class="img-slot"
          @click="navigateToAlbums"
        >
          <img
            :src="`/_cfi/cdn-cgi/image/f=auto,fit=cover,g=auto,quality=80,w=360,h=270${heroImage.url.masterImage}`"
            :srcset="
              transformImageSrcSet(
                `/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=360,h=270${heroImage.url.masterImage} 360w, /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=30,w=720,h=540${heroImage.url.masterImage} 720w`,
              )
            "
            :alt="capitalize(photoCaptionAltFormatter(heroImage.caption))"
            width="360"
            height="270"
          />
        </div>
        <div class="img-slot" @click.stop="mapLocationClick">
          <pu-map-static :centre="staticMapProps.center" :zoom="12" />
        </div>
      </div>
      <lazy-campsite-detail-free-listing-promo v-else class="listing-promo" />
    </div>
  </section>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return */
import { PhotoLoaderService } from '~/common/photos/photoLoader'
import { transformImageSrcSet } from '~/utility/image-url'
import { photoCaptionAltFormatter } from '~/utility/photoCaptionAltFormatter'
import { capitalize } from '~/utility/text'
import { isAbortError } from '~/utility/isAbortError'
import { campsiteToMapCampsite } from '../../campsites/maps'
import type { SearchFilters } from '../../search/schemas'
import { CampsiteDetails } from '../types'
import { getPrimaryPhotoFromPitchType } from './getPrimaryPhoto'
import { getPointFromStringOrDefault } from '../../../utility/geo/latLng'

const { $puScrollTo, $i18n } = useNuxtApp()
const router = useRouter()
const route = useRoute()

const props = defineProps<{
  campsite: CampsiteDetails
  isDated: boolean
  types: number[]
  hasAvailability: boolean
  searchFilters?: SearchFilters
}>()

const { data: photos, error } = await useAsyncData(
  `CampsiteDetailHeroDesktop-${props.campsite.id}-${$i18n.locale}`,
  async () => {
    const photoLoaderService = new PhotoLoaderService()
    const photos = await photoLoaderService.loadPhotos(
      props.campsite.id,
      $i18n.locale,
    )
    return photos
  },
  // this stops the double fetch from server and client
  {
    server: true,
    lazy: process.client,
    dedupe: 'cancel',
    getCachedData: (key, nuxt) => nuxt.payload.data[key] ?? undefined,
  },
)

if (error.value) {
  // Filter out AbortErrors from navigation-based request cancellation
  if (!isAbortError(error.value)) {
    console.error(error.value)
  }
}

const scrollOffset = -30

const _mapCampsite = campsiteToMapCampsite(props.campsite)

const staticMapProps = computed(() => {
  const center = props.campsite.position
    ? props.campsite.position
    : getPointFromStringOrDefault(props.campsite.point)

  return {
    center,
  }
})

function mapLocationClick() {
  $puScrollTo('#location', {
    duration: 200,
    offset: scrollOffset,
  })
}

function navigateToAlbums() {
  void router.push({
    name: 'campsite-albums-album',
    params: route.params,
    query: route.query,
  })
}

const primaryPhoto = getPrimaryPhotoFromPitchType(
  props.campsite,
  props.searchFilters?.categoryIds,
)

const secondaryHeroImages = computed(() => {
  return photos.value?.length ? photos.value?.slice(1, 4) : []
})
</script>

<style scoped>
/* desktop only */
.cs-hero {
  margin: auto;
  max-width: var(--page-width-max);
  padding: 0 var(--page-width-padding) 1.4em;
  width: 100%;
}

header {
  margin: 1em 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 2em;
}

header h1 {
  margin: 0;
}

.booking-info {
  display: flex;
  flex-direction: column;
  row-gap: 0.3em;
  cursor: pointer;
  margin: 0 2em 0 auto;
  flex-shrink: 0;
}

.reviews-info {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
}

.reviews-meta {
  white-space: nowrap;
  color: var(--colour-text-light);
  display: block;
  margin-top: 0.25em;
  font-size: 0.9em;
}

.header-cta {
  background: var(--colour-light-green);
  color: #fff;
  font-weight: bold;
  padding: 0.5em 0.8em;
  border-radius: 6em;
  cursor: pointer;
  margin: auto;
}

.header-cta:hover,
.header-cta:focus-visible {
  background: var(--colour-dark-green);
  outline: none;
}

.cs-hero .rating-bubble {
  font-size: 1.6em;
}

.cs-hero .rating-bubble:before {
  content: '';
}

.hero {
  --hero-gap: 0.6em;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: var(--hero-gap);
}

.hero :deep(.new-campsite) {
  font-size: inherit;
}

.primary-image,
.secondary-image {
  aspect-ratio: 4 / 3;
  width: calc(50% - 0.6em);
}

.primary-image {
  border-radius: var(--hero-gap);
  overflow: hidden;
  position: relative;
}

.primary-image :deep(.flickity-enabled:not(.is-fullscreen)) {
  cursor: pointer;
}

.secondary-image {
  gap: var(--hero-gap);
  display: grid;
  grid-template-columns: 50% 50%;
  grid-template-rows: 1fr 1fr;
}

.no-primary {
  display: none;
}

.img-slot {
  background: var(--colour-page-background);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  z-index: 1;
  cursor: pointer;
}

.img-slot img {
  height: 100%;
  width: 100%;
  object-fit: cover;
}

.img-slot:first-of-type {
  border-radius: var(--hero-gap) 0 0 0;
}

.img-slot:last-of-type {
  border-radius: 0 0 var(--hero-gap) 0;
}

.img-slot:nth-child(2) {
  border-radius: 0 var(--hero-gap) 0 0;
}

.img-slot:nth-child(3) {
  border-radius: 0 0 0 var(--hero-gap);
}

.listing-promo {
  align-self: stretch;
  border-radius: var(--hero-gap);
  margin: 0;
}

.img-slot :deep(.leaflet-control-attribution) {
  display: none;
}
</style>
